import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import ReadOnlyChatView, {
  ReadOnlyChatMessage,
} from '../../digitalTwin/components/ReadOnlyChatView';
import { fetchChatMessages } from './api';

/**
 * The conversation behind a chat classification.
 *
 * Renders through the product's own read-only transcript view — the same one `/rate` shows its
 * raters — so an admin judges what the customer actually saw rather than a second rendition of it.
 * Fetches its own messages, mirroring how SimulationResultsContent behaves for the simulation side.
 */

const ChatTranscript: React.FC<{ sessionId: number }> = ({ sessionId }) => {
  const [messages, setMessages] = useState<ReadOnlyChatMessage[] | null>(null);
  const [title, setTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // No setState before the first await: doing it synchronously inside an effect cascades a render
  // (and trips the lint rule that catches it). The component is remounted per session by its key,
  // so the initial null state is already the loading state — nothing to reset.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await fetchChatMessages(sessionId);
        if (cancelled) return;
        setMessages((body.messages ?? []) as ReadOnlyChatMessage[]);
        setTitle(body.title);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load the conversation');
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  if (error) {
    return <p className="px-6 py-10 text-center text-sm text-red-600">{error}</p>;
  }
  if (messages === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
      </div>
    );
  }
  if (messages.length === 0) {
    return <p className="px-6 py-10 text-center text-sm text-gray-500">This session has no messages.</p>;
  }
  return <ReadOnlyChatView messages={messages} title={title} />;
};

export default ChatTranscript;
